import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { Spinner, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faBuilding, faBriefcase, faUser, faPhone,
  faCalendarAlt, faSyncAlt, faExclamationTriangle, faCheckCircle,
  faTrash, faSpinner, faEnvelope, faIdCard, faPlus,
  faFilter, faTimes, faChartPie, faUsers
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import { PlacementContext } from '../layout/CunstomEntretiens';
import EmployeFormModal from './EmployeFormModal';

// ============================================================
// UTILITAIRE FETCH JSON
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200)}`);
  }
};

// ============================================================
// CACHE DEXIE (24h)
// ============================================================
const db = new Dexie('PlacementCacheDB');
db.version(1).stores({
  placements: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedPlacements(key, allowExpired = false) {
  try {
    const entry = await db.placements.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCachedPlacements(key, data) {
  await db.placements.put({ key, data, timestamp: Date.now() });
}
const getStalePlacements = (key) => getCachedPlacements(key, true);

const API_URL = `${API_CONFIG2.apiUrl}placement/api_placement_list.php`;

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES
// ============================================================
const pendingFetches = new Map();

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const PlacementList = () => {
  const context = useContext(PlacementContext) || {};
  const { refreshKey, setSource } = context;

  // États principaux
  const [placements, setPlacements] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [deleting, setDeleting] = useState({});
  const [dataSource, setDataSource] = useState('cache');

  // États pour les filtres
  const [filters, setFilters] = useState({
    search: '',
    statut: '',
    date_debut: '',
    date_fin: '',
    entreprise: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Modal employé
  const [showEmployeModal, setShowEmployeModal] = useState(false);
  const [selectedCandidat, setSelectedCandidat] = useState(null);

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      if (isMounted.current) setToast(prev => ({ ...prev, show: false }));
    }, duration);
  }, []);

  // ============================================================
  // RÉCUPÉRATION DES STATISTIQUES
  // ============================================================
  const fetchStats = useCallback(async () => {
    try {
      const data = await fetchJSON(`${API_URL}?action=stats`);
      if (data.status === 'success') {
        setStats(data.data);
      }
    } catch (err) {
      console.warn('Statistiques non disponibles', err);
    }
  }, []);

  // ============================================================
  // RÉCUPÉRATION DES PLACEMENTS (avec filtres)
  // ============================================================
  const fetchPlacements = useCallback(async (page = 1, filtersObj = null, forceRefresh = false, retryCount = 0) => {
    // Si filtersObj non fourni, utiliser l'état actuel
    const currentFilters = filtersObj || filters;

    // Construire les paramètres de requête
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', pagination.limit);
    if (currentFilters.search) params.append('search', currentFilters.search);
    if (currentFilters.statut) params.append('statut', currentFilters.statut);
    if (currentFilters.date_debut) params.append('date_debut', currentFilters.date_debut);
    if (currentFilters.date_fin) params.append('date_fin', currentFilters.date_fin);
    if (currentFilters.entreprise) params.append('entreprise', currentFilters.entreprise);

    const cacheKey = params.toString() || 'default';

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setPlacements(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);

    const fetchPromise = (async () => {
      try {
        if (!forceRefresh) {
          const cached = await getCachedPlacements(cacheKey);
          if (cached && isMounted.current) {
            setPlacements(cached.data);
            setPagination(cached.pagination);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        const url = `${API_URL}?${params.toString()}`;
        const response = await fetch(url, { signal: controller.signal });

        if (response.status === 429) {
          const stale = await getStalePlacements(cacheKey);
          if (stale && isMounted.current) {
            setPlacements(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache périmé', 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchPlacements(page, currentFilters, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStalePlacements(cacheKey);
          if (stale && isMounted.current) {
            setPlacements(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast(`Erreur réseau – Utilisation du cache`, 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setPlacements(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await setCachedPlacements(cacheKey, result);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        const stale = await getStalePlacements(cacheKey);
        if (stale && isMounted.current) {
          setPlacements(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(`Erreur : ${err.message}`, 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, filters, setSource, showToast]);

  // ============================================================
  // GESTION DES FILTRES
  // ============================================================
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    fetchPlacements(1, filters, false);
  };

  const resetFilters = () => {
    setFilters({ search: '', statut: '', date_debut: '', date_fin: '', entreprise: '' });
    fetchPlacements(1, { search: '', statut: '', date_debut: '', date_fin: '', entreprise: '' }, true);
  };

  // ============================================================
  // SUPPRESSION D'UN PLACEMENT
  // ============================================================
  const handleDelete = async (id) => {
    if (!id) {
      showToast('ID du placement manquant', 'danger');
      return;
    }
    if (!window.confirm('⚠️ Supprimer définitivement ce placement ?')) return;

    setDeleting(prev => ({ ...prev, [id]: true }));
    try {
      const data = await fetchJSON(`${API_URL}?id=${id}`, { method: 'DELETE' });
      if (data.status === 'success') {
        showToast('Placement supprimé ✓', 'success');
        await fetchPlacements(pagination.currentPage, filters, true);
        await fetchStats();
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      showToast(err.message, 'danger');
    } finally {
      setDeleting(prev => ({ ...prev, [id]: false }));
    }
  };

  // ============================================================
  // OUVERTURE DU MODAL EMPLOYÉ
  // ============================================================
  const openEmployeModal = (candidatData) => {
    setSelectedCandidat({
      nom: candidatData.nom || '',
      prenom: candidatData.prenom || '',
      postnom: candidatData.postnom || '',
      date_naissance: candidatData.date_naissance || '',
      telephone: candidatData.tel || '',
      email: candidatData.email || '',
      adresse: candidatData.adresse || '',
      ville: candidatData.ville || '',
      sexe: candidatData.sexe || '',
      type_contrat: candidatData.type_contrat || '',
      entreprise: candidatData.entreprise_nom || '',
      id: candidatData.entreprise_id || '',
    });
    setShowEmployeModal(true);
  };

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchStats();
    fetchPlacements(1, filters, false);
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
  }, []);

  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchPlacements(pagination.currentPage, filters, true);
      fetchStats();
    }
  }, [refreshKey, fetchPlacements, pagination.currentPage, filters]);

  // ============================================================
  // RENDU
  // ============================================================
  const totalStatuses = stats.by_status || {};
  const statusColors = {
    'Actif': 'success',
    'Terminé': 'secondary',
    'En attente': 'warning',
    'Annulé': 'danger'
  };

  if (loading && placements.length === 0) {
    return (
      <div className="placement-list">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-secondary mt-2">Chargement des placements...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .placement-list {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          font-weight: 500;
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.25);
        }
        .monetra-btn.icon-only {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .search-wrapper {
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 12px;
          margin-bottom: 8px;
        }
        .stat-item {
          background: rgba(255,255,255,0.03);
          border-radius: 12px;
          padding: 10px 12px;
          text-align: center;
          border: 1px solid rgba(255,255,255,0.04);
        }
        .stat-item .number {
          font-size: 1.5rem;
          font-weight: 700;
          color: #fff;
        }
        .stat-item .label {
          font-size: 0.7rem;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.2rem;
          margin-top: 0.5rem;
        }

        .placement-card {
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: var(--radius);
          padding: 1.2rem;
          transition: all 0.3s cubic-bezier(0.2, 0, 0, 1);
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
        }
        .placement-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; height: 2px;
          background: linear-gradient(90deg, transparent, var(--accent-blue), transparent);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .placement-card:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(59, 130, 246, 0.2);
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.3);
        }
        .placement-card:hover::before {
          opacity: 1;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        }
        .card-id {
          font-size: 0.7rem;
          color: var(--text-muted);
          background: rgba(255,255,255,0.04);
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 600;
        }
        .statut-badge {
          padding: 2px 10px;
          border-radius: 20px;
          font-size: 0.65rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        .statut-badge.actif { background: rgba(46,204,113,0.2); color: #2ecc71; }
        .statut-badge.termine { background: rgba(148,163,184,0.2); color: #94a3b8; }
        .statut-badge.en-attente { background: rgba(245,158,11,0.2); color: #f59e0b; }
        .statut-badge.annule { background: rgba(239,68,68,0.2); color: #ef4444; }
        .statut-badge.default { background: rgba(255,255,255,0.05); color: var(--text-secondary); }

        .entreprise-nom {
          font-weight: 600;
          color: #fff;
          font-size: 1.05rem;
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 4px;
        }
        .offre-desc {
          color: var(--text-secondary);
          font-size: 0.9rem;
          margin-bottom: 8px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .contrat-badge {
          display: inline-block;
          padding: 2px 12px;
          background: rgba(255,255,255,0.04);
          border-radius: 20px;
          font-size: 0.7rem;
          color: var(--text-secondary);
          border: 1px solid rgba(255,255,255,0.04);
          font-weight: 500;
        }
        .candidat-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          margin: 8px 0;
          font-size: 0.9rem;
          color: var(--text-secondary);
        }
        .candidat-info .name {
          color: var(--text-primary);
          font-weight: 500;
        }
        .candidat-info .detail {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .date-placement {
          font-size: 0.75rem;
          color: var(--text-muted);
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 4px;
        }
        .card-actions {
          display: flex;
          justify-content: space-between;
          gap: 6px;
          margin-top: 12px;
          padding-top: 10px;
          border-top: 1px solid rgba(255,255,255,0.04);
        }

        .filter-bar {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: flex-end;
          margin-top: 12px;
        }
        .filter-group {
          flex: 1 1 160px;
          min-width: 120px;
        }
        .filter-group label {
          display: block;
          font-size: 0.7rem;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        .filter-group input, .filter-group select {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 8px 14px;
          color: var(--text-primary);
          font-size: 0.9rem;
          outline: none;
          transition: 0.3s;
        }
        .filter-group input:focus, .filter-group select:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
        }
        .filter-group input::placeholder {
          color: var(--text-muted);
        }
        .filter-group select option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .toast-container {
          position: fixed;
          top: clamp(12px, 2vh, 24px);
          right: clamp(12px, 2vw, 24px);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 12px 18px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent; border: none; color: var(--text-muted);
          cursor: pointer; font-size: 1.2rem; padding: 0 4px; margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover { color: var(--text-primary); }

        .flex-between { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; }
        .gap-2 { gap: 8px; }
        .mb-3 { margin-bottom: clamp(1rem, 2vw, 1.5rem); }
        .mt-2 { margin-top: 0.5rem; }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-center { text-align: center; }
        .fw-bold { font-weight: 600; }
        .small { font-size: clamp(0.65rem, 0.8vw, 0.8rem); }
        .display-6 { font-size: clamp(1.6rem, 4vw, 2.2rem); font-weight: 700; }
        .m-0 { margin: 0; }
        .p-3 { padding: 1rem; }
        .p-0 { padding: 0; }
        .overflow-hidden { overflow: hidden; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }

        @media (max-width: 768px) {
          .placement-list { padding: 12px; }
          .glass-card { padding: 1rem; }
          .flex-between { flex-direction: column; align-items: stretch; }
          .filter-bar { flex-direction: column; align-items: stretch; }
          .filter-group { flex: 1 1 100%; }
          .cards-grid { grid-template-columns: 1fr; }
          .card-actions { flex-wrap: wrap; }
          .card-actions .monetra-btn { flex: 1; }
        }
        @media (max-width: 480px) {
          .monetra-btn { font-size: 0.65rem; padding: 4px 10px; min-height: 34px; }
          .placement-card { padding: 0.8rem; }
          .stats-grid { grid-template-columns: 1fr 1fr; }
        }
      `}</style>

      <div className="placement-list">
        {/* En-tête */}
        <div className="flex-between mb-3">
          <div>
            <h1 className="display-6 fw-bold mb-1">📋 Placements</h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Liste des candidats placés sur des offres d'emploi
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn"
              onClick={() => fetchPlacements(pagination.currentPage, filters, false)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
            <button
              className="monetra-btn"
              onClick={() => fetchPlacements(pagination.currentPage, filters, true)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="glass-card mb-3">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="number">{stats.total || 0}</div>
              <div className="label">Total placements</div>
            </div>
            {Object.entries(totalStatuses).map(([status, count]) => (
              <div key={status} className="stat-item">
                <div className="number">{count}</div>
                <div className="label">{status}</div>
              </div>
            ))}
            {Object.keys(totalStatuses).length === 0 && (
              <div className="stat-item">
                <div className="number">—</div>
                <div className="label">Aucun statut</div>
              </div>
            )}
          </div>
        </div>

        {/* Filtres */}
        <div className="glass-card mb-3">
          <div className="flex-between">
            <div className="d-flex gap-2 align-items-center">
              <button
                className="monetra-btn"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FontAwesomeIcon icon={faFilter} /> {showFilters ? 'Masquer filtres' : 'Afficher filtres'}
              </button>
              {showFilters && (
                <button className="monetra-btn" onClick={resetFilters}>
                  <FontAwesomeIcon icon={faTimes} /> Réinitialiser
                </button>
              )}
            </div>
            <div className="text-secondary small">
              <span className="fw-bold">{pagination?.totalRecords || 0}</span> placements
            </div>
          </div>

          {showFilters && (
            <div className="filter-bar">
              <div className="filter-group">
                <label>Recherche</label>
                <input
                  type="text"
                  name="search"
                  placeholder="Nom, entreprise..."
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="filter-group">
                <label>Statut</label>
                <select name="statut" value={filters.statut} onChange={handleFilterChange}>
                  <option value="">Tous</option>
                  <option value="Actif">Actif</option>
                  <option value="Terminé">Terminé</option>
                  <option value="En attente">En attente</option>
                  <option value="Annulé">Annulé</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Entreprise</label>
                <input
                  type="text"
                  name="entreprise"
                  placeholder="Nom entreprise"
                  value={filters.entreprise}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="filter-group">
                <label>Date début</label>
                <input type="date" name="date_debut" value={filters.date_debut} onChange={handleFilterChange} />
              </div>
              <div className="filter-group">
                <label>Date fin</label>
                <input type="date" name="date_fin" value={filters.date_fin} onChange={handleFilterChange} />
              </div>
              <div className="filter-group" style={{ flex: '0 0 auto' }}>
                <button className="monetra-btn primary" onClick={applyFilters} disabled={loading || refreshing}>
                  <FontAwesomeIcon icon={faSearch} /> Appliquer
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Grille de cartes */}
        <div className="glass-card p-0 overflow-hidden">
          {loading && !refreshing ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : placements.length === 0 ? (
            <div className="text-center py-5 text-secondary">Aucun placement trouvé.</div>
          ) : (
            <div className="cards-grid" style={{ padding: '1rem' }}>
              {placements.map(item => (
                <div key={item.id} className="placement-card">
                  <div className="card-header">
                    <span className="card-id">#{item.id}</span>
                    <span className={`statut-badge ${(item.statut || 'Actif').toLowerCase().replace(' ', '-')}`}>
                      {item.statut || 'Actif'}
                    </span>
                  </div>

                  <div className="entreprise-nom">
                    <FontAwesomeIcon icon={faBuilding} className="text-white" />
                    {item.entreprise?.nom || 'Entreprise inconnue'} #ID: {item.entreprise?.id || 'Entreprise inconnue'} 
                  </div>

                  <div className="offre-desc">
                    <FontAwesomeIcon icon={faBriefcase} className="me-1 text-white" />
                    {item.offre?.description || 'Offre sans description'}
                  </div>

                  <div className="candidat-info">
                    <div className="name">
                      <FontAwesomeIcon icon={faUser} className="me-1 text-white" />
                      {item.candidat?.prenom || ''} {item.candidat?.nom || 'Candidat inconnu'}
                    </div>
                    <div className="detail">
                      <FontAwesomeIcon icon={faPhone} className="text-white" style={{ width: '16px' }} />
                      {item.candidat?.tel || '—'}
                    </div>
                    <div className="detail">
                      <FontAwesomeIcon icon={faEnvelope} className="text-white" style={{ width: '16px' }} />
                      {item.candidat?.email || '—'}
                    </div>
                  </div>

                  <div className="date-placement">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {new Date(item.date_placement).toLocaleDateString('fr-FR', {
                      day: '2-digit', month: 'short', year: 'numeric'
                    })}
                    <span className="contrat-badge" style={{ marginLeft: 'auto' }}>
                      {item.offre?.type_contrat || 'CDI'}
                    </span>
                  </div>

                  <div className="card-actions">
                    <button
                      className="monetra-btn primary"
                      onClick={() => openEmployeModal(item.candidat)}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Ajouter employé
                    </button>
                    <button
                      className="monetra-btn icon-only danger"
                      onClick={() => handleDelete(item.id)}
                      disabled={deleting[item.id] || loading || refreshing}
                      title="Supprimer"
                    >
                      {deleting[item.id] ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon icon={faTrash} />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          <div className="p-3 border-top border-secondary border-opacity-10 flex-between">
            <span className="text-secondary small">
              Page {pagination?.currentPage || 1} sur {pagination?.totalPages || 1}
            </span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn"
                disabled={!pagination || pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchPlacements(pagination.currentPage - 1, filters, false)}
              >
                ‹
              </button>
              <button
                className="monetra-btn"
                disabled={!pagination || pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchPlacements(pagination.currentPage + 1, filters, false)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL EMPLOYÉ */}
      <EmployeFormModal
        show={showEmployeModal}
        onHide={() => {
          setShowEmployeModal(false);
          setSelectedCandidat(null);
        }}
        onSuccess={() => {
          fetchPlacements(pagination.currentPage, filters, true);
          fetchStats();
        }}
        initialData={selectedCandidat}
      />

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default PlacementList;